import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Divider, Switch, FormControlLabel, Button, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, RadioGroup, Radio } from '@mui/material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Settings: React.FC = () => {
  const { darkMode, toggleDarkMode } = useCustomTheme();
  const { logout } = useAuth();
  const { language, setLanguage } = useLanguage();
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(() => {
    const raw = localStorage.getItem('notificationsEnabled');
    return raw ? JSON.parse(raw) : true;
  });
  const [browserNotificationPermission, setBrowserNotificationPermission] = useState<string>(() => (
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
  ));
  const [timezone, setTimezone] = useState<string>(() => localStorage.getItem('timezone') || 'local');
  const [dateFormat, setDateFormat] = useState<string>(() => localStorage.getItem('dateFormat') || 'MM/DD/YYYY');
  const [fontSize, setFontSize] = useState<string>(() => localStorage.getItem('fontSize') || 'medium');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState<boolean>(() => {
    const raw = localStorage.getItem('twoFactorEnabled');
    return raw ? JSON.parse(raw) : false;
  });
  const [shareProfile, setShareProfile] = useState<boolean>(() => {
    const raw = localStorage.getItem('shareProfile');
    return raw ? JSON.parse(raw) : false;
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success'|'info'|'error'>('info');
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  
  const navigate = useNavigate();

  const requestBrowserNotifications = async () => {
    if (typeof Notification === 'undefined') {
      setSnackbarMessage('This browser does not support desktop notifications. In-app reminders will still be available.');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setBrowserNotificationPermission(permission);
      setSnackbarMessage(
        permission === 'granted'
          ? 'Browser notifications are enabled.'
          : 'Browser notifications were not enabled. In-app reminders will be used instead.'
      );
      setSnackbarSeverity(permission === 'granted' ? 'success' : 'info');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Unable to request browser notification permission:', error);
      setSnackbarMessage('Could not request browser notification permission. Check your browser settings.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    localStorage.setItem('timezone', timezone);
  }, [timezone]);

  useEffect(() => {
    localStorage.setItem('dateFormat', dateFormat);
  }, [dateFormat]);

  useEffect(() => {
    localStorage.setItem('fontSize', fontSize);
    // apply a simple CSS class on the html element for accessibility/font size
    try {
      document.documentElement.style.fontSize = fontSize === 'small' ? '14px' : fontSize === 'large' ? '18px' : '';
    } catch (e) {}
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem('notificationsEnabled', JSON.stringify(notificationsEnabled));
  }, [notificationsEnabled]);

  useEffect(() => {
    localStorage.setItem('twoFactorEnabled', JSON.stringify(twoFactorEnabled));
  }, [twoFactorEnabled]);

  useEffect(() => {
    localStorage.setItem('shareProfile', JSON.stringify(shareProfile));
  }, [shareProfile]);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: { xs: 2, sm: 3, md: 4 }, p: { xs: 1, sm: 2 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        Settings
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2 }} elevation={1}>
        <Typography variant="h6">Appearance</Typography>
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
          Toggle between Light and Dark themes. Your choice will be saved for this browser.
        </Typography>
        <FormControlLabel
          control={<Switch checked={darkMode} onChange={toggleDarkMode} color="primary" />}
          label={darkMode ? 'Dark mode' : 'Light mode'}
        />

        <Divider sx={{ my: 3 }} />

        <Button variant="outlined" color="secondary" onClick={() => {
          // allow user to reset theme preference
          localStorage.removeItem('darkMode');
          window.location.reload();
        }}>
          Reset Theme Preference
        </Button>
        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mt: 2 }}>Preferences</Typography>
        <FormControl sx={{ mt: 1, minWidth: 220 }} size="small">
          <InputLabel id="language-select-label">Language</InputLabel>
          <Select
            labelId="language-select-label"
            value={language}
            label="Language"
            onChange={(e) => setLanguage(e.target.value as typeof language)}
          >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="es">Spanish</MenuItem>
            <MenuItem value="fr">French</MenuItem>
            <MenuItem value="de">German</MenuItem>
            <MenuItem value="zh-CN">Chinese</MenuItem>
            <MenuItem value="ha">Hausa</MenuItem>
            <MenuItem value="ig">Igbo</MenuItem>
            <MenuItem value="yo">Yoruba</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          sx={{ display: 'block', mt: 2 }}
          control={<Switch checked={notificationsEnabled} onChange={() => setNotificationsEnabled(v => !v)} color="primary" />}
          label={notificationsEnabled ? 'Notifications enabled' : 'Notifications disabled'}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Language Hub reminders repeat every 30 minutes while you are active until you visit the Word of the Day and Idioms pages.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Browser status: {browserNotificationPermission === 'unsupported' ? 'not supported' : browserNotificationPermission}
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={requestBrowserNotifications}
          disabled={browserNotificationPermission === 'granted' || browserNotificationPermission === 'unsupported'}
        >
          Enable browser notifications
        </Button>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          If browser notifications are unavailable or denied, reminders appear inside the platform. Browser alerts require this site to be open.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ mt: 2 }}>Timezone</Typography>
        <FormControl sx={{ mt: 1, minWidth: 220 }} size="small">
          <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            <MenuItem value="local">Local (browser)</MenuItem>
            <MenuItem value="UTC">UTC</MenuItem>
            <MenuItem value="America/New_York">America / New York</MenuItem>
            <MenuItem value="Europe/London">Europe / London</MenuItem>
            <MenuItem value="Asia/Shanghai">Asia / Shanghai</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="subtitle1" sx={{ mt: 2 }}>Date format</Typography>
        <FormControl sx={{ mt: 1, minWidth: 220 }} size="small">
          <Select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
            <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
            <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
            <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="subtitle1" sx={{ mt: 2 }}>Accessibility</Typography>
        <RadioGroup row value={fontSize} onChange={(e) => setFontSize(e.target.value)}>
          <FormControlLabel value="small" control={<Radio />} label="Small" />
          <FormControlLabel value="medium" control={<Radio />} label="Medium" />
          <FormControlLabel value="large" control={<Radio />} label="Large" />
        </RadioGroup>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ mt: 2 }}>Security & Privacy</Typography>
        <FormControlLabel
          sx={{ display: 'block', mt: 1 }}
          control={<Switch checked={twoFactorEnabled} onChange={() => setTwoFactorEnabled(v => !v)} color="primary" />}
          label={twoFactorEnabled ? 'Two-factor authentication enabled (UI only)' : 'Two-factor authentication disabled'}
        />

        <FormControlLabel
          sx={{ display: 'block', mt: 1 }}
          control={<Switch checked={shareProfile} onChange={() => setShareProfile(v => !v)} color="primary" />}
          label={shareProfile ? 'Profile is public' : 'Profile is private'}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ mt: 2 }}>Data</Typography>
        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => {
            setSnackbarMessage('Data export requested. You will receive an email when ready.');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
          }}>Request Data Export</Button>
          <Button color="error" variant="outlined" onClick={() => setDeleteAccountOpen(true)}>Delete My Account</Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" color="warning" onClick={() => {
            // clear stored preferences
            localStorage.removeItem('language');
            localStorage.removeItem('notificationsEnabled');
            localStorage.removeItem('darkMode');
            localStorage.removeItem('timezone');
            localStorage.removeItem('dateFormat');
            localStorage.removeItem('fontSize');
            localStorage.removeItem('twoFactorEnabled');
            localStorage.removeItem('shareProfile');
            setLanguage('en');
            setNotificationsEnabled(true);
            setTimezone('local');
            setDateFormat('MM/DD/YYYY');
            setFontSize('medium');
            setTwoFactorEnabled(false);
            setShareProfile(false);
            window.location.reload();
          }}>
            Reset Preferences
          </Button>
        </Box>
      </Paper>

      <Dialog open={deleteAccountOpen} onClose={() => setDeleteAccountOpen(false)}>
        <DialogTitle>Delete account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deleting your account will remove your profile and data from this site. This action cannot be undone. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAccountOpen(false)}>Cancel</Button>
          <Button color="error" onClick={async () => {
            try {
              localStorage.clear();
              if (logout) await logout();
            } catch (e) {
              // ignore
            }
            navigate('/');
            setSnackbarMessage('Account deletion requested locally. If server-side deletion is supported it will be processed.');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
            setDeleteAccountOpen(false);
          }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
